import { Box, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { FieldCellValueUI, FormFieldValueGroupUI, FormFieldValuesUI } from "Backend";
import { OldLogo } from "components/Icons";
import { CaseFormContext } from "contexts/CaseFormContext";
import { FieldExportValueFilter, FieldValueGroupSelectionStatus, FormField } from "generated/graphql";
import { Fragment, useContext } from "react";
import { useAppSelector } from "redux/hooks";
import { selectClientLens } from "redux/slices/clientLens";
import { selectFormFields } from "redux/slices/form.slice";
import theme from "theme";
import { P } from "theme/typography";
import { FieldExportConfigUI, ProfileExportConfigUI } from "./ExportConfigInterfaces";
import displayCellValue from "components/FormField/displayCellValue";

interface ProfileExportConfigPreviewProps {
  caseData: { clientCaseId: string; caseId: number; claimantName: string };
  profileExportConfig: ProfileExportConfigUI;
}

interface ResponsesProps {
  fieldConfig: FieldExportConfigUI;
  field: FormField;
  fieldValue: FormFieldValuesUI;
}

const responseText = (body: { noResponseText: string; yesNoValue?: boolean | null }): string => {
  const { noResponseText, yesNoValue } = body;

  if (yesNoValue !== undefined && yesNoValue !== null) {
    return yesNoValue ? "Yes" : "No";
  } else {
    return noResponseText;
  }
};

const ProfileExportConfigPreview = ({ caseData, profileExportConfig }: ProfileExportConfigPreviewProps) => {
  const caseForm = useContext(CaseFormContext);
  const formFields = useAppSelector(selectFormFields);
  const lens = useAppSelector(selectClientLens);

  const Responses = ({ fieldConfig, field, fieldValue }: ResponsesProps) => {
    const valueFilter = fieldConfig.valueFilter;
    const optionFilter = (valueFilter: FieldExportValueFilter, cellValue: FieldCellValueUI) =>
      valueFilter.options.includes(cellValue.value.textValue || "") ||
      valueFilter.options.some((option) => cellValue.value.multiSelectValue?.includes(option));
    const cellValueFilter = (valueGroup: FormFieldValueGroupUI) =>
      field.cells.some((cell, cellIndex) => {
        const cellValue = valueGroup.values[cellIndex];
        if (valueFilter && cellValue) {
          return (
            valueFilter.cellId === cell.id &&
            (valueFilter.negated ? !optionFilter(valueFilter, cellValue) : optionFilter(valueFilter, cellValue))
          );
        } else return true;
      });
    const starredFilter = (valueGroup: FormFieldValueGroupUI) => {
      if (fieldConfig.selectedOnly) {
        return valueGroup.selectionStatus === FieldValueGroupSelectionStatus.Selected;
      } else return true;
    };
    const tagFilter = (valueGroup: FormFieldValueGroupUI) => {
      const configTags = fieldConfig.tagFilter?.tags || [];
      if (configTags.length > 0) {
        return valueGroup.tags.some((tag) => configTags.includes(tag.systemName));
      } else return true;
    };
    const valueGroupFilter = (valueGroup: FormFieldValueGroupUI) =>
      cellValueFilter(valueGroup) && starredFilter(valueGroup) && tagFilter(valueGroup);
    const filteredFieldValues = {
      ...fieldValue,
      valueGroups: fieldValue.valueGroups.filter(valueGroupFilter),
    };
    return filteredFieldValues?.valueGroups.length || !profileExportConfig.skipNoResponse ? (
      <Box sx={{ mb: 2 }} key={fieldConfig.fieldId}>
        <Box
          sx={{
            backgroundColor: "#e0e2e6",
            p: 1,
            mb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <P
            variant="h5"
            sx={{
              // smaller font size than default
              fontSize: "1rem",
              // capitalize first letter
              textTransform: "uppercase",
            }}>
            {field.outlinePrefix} {field.name}
          </P>
        </Box>
        <Box display="flex">
          <P variant="body4" sx={{ pl: 4 }}>
            {field.prompt}
          </P>
          <P
            variant="body4"
            sx={{
              pl: 1,
            }}>
            {!field.promptYesNo
              ? ""
              : responseText({
                  noResponseText: profileExportConfig.noResponseText || "No Response",
                  yesNoValue: filteredFieldValues?.yesNoValue,
                })}
          </P>
        </Box>
        {fieldConfig.layout === "form" ? (
          (filteredFieldValues?.valueGroups || []).map((valueGroup, index) => {
            return (
              <Box
                key={valueGroup.id}
                sx={{ ml: 4, mb: 3, pl: 1, mt: 2, borderLeft: `3px solid ${theme.palette.grey[100]}` }}>
                <P variant="h6">Response {index + 1}</P>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    mt: 2,
                    gap: "32px 16px",
                  }}>
                  {field.cells.map((cell, cellIndex) => {
                    const cellValue = valueGroup.values[cellIndex];
                    const stringValue = cellValue !== undefined ? displayCellValue(cellValue.value) : "";
                    return fieldConfig.cellIds?.includes(cell.id) ? (
                      <Box key={cell.id} sx={{ display: "flex", flexDirection: "column" }}>
                        <P variant="subtitle1heavy" sx={{ color: "grey.400", fontSize: 14 }}>
                          {cell.name}
                        </P>
                        <P variant="subtitle1">{stringValue}</P>
                      </Box>
                    ) : (
                      <Fragment key={cell.id}></Fragment>
                    );
                  })}
                </Box>
                {valueGroup.references && (
                  <Box sx={{ display: "grid", mt: 4, gridTemplateColumns: "1fr 100px" }}>
                    <div>
                      <P variant="subtitle1heavy" sx={{ my: 2, color: "grey.400", fontSize: 14 }}>
                        Document References
                      </P>
                      {valueGroup.references.map((ref) => (
                        <P key={ref.id} variant="subtitle2">
                          {ref.documentName}
                        </P>
                      ))}
                    </div>
                    <Box sx={{ borderLeft: `2px solid ${theme.palette.grey[100]}`, pl: 2 }}>
                      <P variant="subtitle1heavy" sx={{ my: 2, color: "grey.400", fontSize: 14 }}>
                        Page
                      </P>
                      {valueGroup.references.map((ref) => (
                        <P
                          key={ref.id}
                          variant="subtitle2"
                          sx={{
                            color: theme.palette.primary.main,
                          }}>
                          {!ref.pageRangeStart && !ref.pageRangeEnd
                            ? `${ref.originalPage}`
                            : `${ref.pageRangeStart}-${ref.pageRangeEnd}`}
                        </P>
                      ))}
                    </Box>
                  </Box>
                )}
                {!fieldConfig.skipNotes && valueGroup.notes && (
                  <Box
                    sx={{
                      mt: 4,
                    }}>
                    <P variant="subtitle1heavy" sx={{ my: 2, color: "grey.400", fontSize: 14 }}>
                      Notes
                    </P>
                    <P variant="subtitle2">{valueGroup.notes}</P>
                  </Box>
                )}
              </Box>
            );
          })
        ) : filteredFieldValues?.valueGroups.length > 0 ? (
          <Box
            sx={{
              px: 2,
              mt: 1,
            }}>
            <Table sx={{ tableLayout: "fixed" }}>
              <TableHead
                sx={{
                  // Gray top and bottom borders
                  borderTop: `3px solid #808080`,
                  borderBottom: `3px solid #808080`,
                  // no hover effect
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}>
                <TableRow
                  sx={{
                    // no hover effect
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}>
                  {field.cells.map((cell) => {
                    return fieldConfig.cellIds?.includes(cell.id) ? (
                      <TableCell
                        key={cell.id}
                        sx={{
                          // smaller vertical padding
                          py: 0.5,
                        }}>
                        <P
                          variant="body5"
                          sx={{
                            fontWeight: 400,
                          }}>
                          {cell.name}
                        </P>
                      </TableCell>
                    ) : (
                      <Fragment key={cell.id}></Fragment>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  // darker divider lines
                  "& .MuiTableCell-root": {
                    borderBottom: `1px solid #e0e2e6`,
                  },
                }}>
                {filteredFieldValues?.valueGroups.map((valueGroup) => {
                  return (
                    <TableRow
                      key={valueGroup.id}
                      sx={{
                        // no hover effect
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}>
                      {field.cells.map((cell, cellIndex) => {
                        const cellValue = valueGroup.values[cellIndex];
                        const stringValue = cellValue !== undefined ? displayCellValue(cellValue.value) : "";
                        return fieldConfig.cellIds?.includes(cell.id) ? (
                          <TableCell key={cell.id}>
                            <P variant="body5">{stringValue}</P>
                          </TableCell>
                        ) : (
                          <Fragment key={cell.id}></Fragment>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <></>
        )}
      </Box>
    ) : (
      <></>
    );
  };

  return (
    <Card>
      <CardContent sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            mb: 1,
          }}>
          <OldLogo size={72} />
        </Box>
        <P variant="h5light" sx={{ mb: 0.5 }}>
          {profileExportConfig.title ? profileExportConfig.title : `${lens.name} Profile`}
        </P>
        <P variant="body4">
          <strong>Case ID: </strong>
          {caseData.clientCaseId}
        </P>
        <P variant="body4" sx={{ mb: 2 }}>
          <strong>Claimant Name: </strong>
          {caseData.claimantName}
        </P>

        {!profileExportConfig.skipSummary && (
          <P variant="body4" sx={{ mb: 2 }}>
            <strong>Summary: </strong>
            {"< summary here >"}
          </P>
        )}
        {(profileExportConfig.fieldConfigs || []).map((fieldConfig) => {
          const field = formFields.find((ff) => ff.id === fieldConfig.fieldId);
          const fieldValueHandle = caseForm.stateHandle[fieldConfig.fieldId];
          if (fieldValueHandle === undefined) {
            return <Fragment key={fieldConfig.fieldId}></Fragment>;
          } else {
            const fieldValue = fieldValueHandle[0];

            return field && !field.exportExclude && fieldConfig.included && fieldValue ? (
              <Responses key={fieldConfig.fieldId} field={field} fieldValue={fieldValue} fieldConfig={fieldConfig} />
            ) : (
              <Fragment key={fieldConfig.fieldId}></Fragment>
            );
          }
        })}
      </CardContent>
    </Card>
  );
};

export default ProfileExportConfigPreview;
